import { Currency } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { ProductId } from "@redotech/redo-ids/ids";
import {
  ProductSelector,
  SelectProductInfoOrCustom,
} from "@redotech/redo-merchant-app-common/components/product-selector";
import { FulfillmentOrderLineItem } from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { WeightUnit } from "@redotech/redo-model/outbound-labels/util";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { ButtonSize, IconButton } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import * as styles from "./item-table.module.css";

const DEFAULT_ITEM: FulfillmentOrderLineItem = {
  externalId: "",
  customAttributes: [],
  sku: "",
  title: "",
  productTitle: "",
  variantTitle: "",
  totalQuantity: 1,
  unfulfilledQuantity: 1,
  weight: { unit: WeightUnit.GRAM, value: 0 },
  lineItemId: crypto.randomUUID(),
  unitPrice: { amount: "1.00", currency: Currency.USD },
  unitDiscountedPrice: { amount: "1.00", currency: Currency.USD },
  totalPrice: { amount: "1.00", currency: Currency.USD },
  totalDiscountedPrice: { amount: "1.00", currency: Currency.USD },
  totalTax: { amount: "0", currency: Currency.USD },
  hsCode: null,
  quantity: 1,
} as const;

export const ItemTable = memo(function ItemTable({
  onItemsChange,
  initialItems,
}: {
  onItemsChange: (items: FulfillmentOrderLineItem[]) => void;
  initialItems?: FulfillmentOrderLineItem[];
}) {
  const [items, setItems] = useState<FulfillmentOrderLineItem[]>(
    initialItems && initialItems.length > 0 ? initialItems : [DEFAULT_ITEM],
  );

  const updateItem = useHandler(
    (index: number, item: FulfillmentOrderLineItem) => {
      setItems((prev) => {
        const newItems = prev.map((existingItem, i) =>
          i === index ? item : existingItem,
        );
        onItemsChange(newItems);
        return newItems;
      });
    },
  );

  const removeItem = useHandler((index: number) => {
    setItems((prev) => {
      const newItems = prev.filter((_, i) => i !== index);
      onItemsChange(newItems);
      return newItems;
    });
  });

  const addOrderItemClick = useHandler(() => {
    setItems((prev) => {
      const newItems = [...prev, DEFAULT_ITEM];
      onItemsChange(newItems);
      return newItems;
    });
  });

  const handleProductSelection = useHandler(
    (index: number, selectedProducts: SelectProductInfoOrCustom[]) => {
      const selectedProduct = selectedProducts[0];
      if (!selectedProduct) {
        return;
      }

      const item = items[index];
      if (!item) {
        return;
      }

      if (selectedProduct.isCustom) {
        updateItem(index, {
          ...item,
          imageUrl: undefined,
          inventoryItemId: null,
          productId: null,
          productTitle: selectedProduct.title,
          redoProductId: null,
          sku: "",
          title: selectedProduct.title,
          variantId: null,
          variantTitle: "",
        });
      } else {
        updateItem(index, {
          ...item,
          externalId: item.externalId || selectedProduct.id,
          imageUrl: selectedProduct.imageUrl ?? item.imageUrl,
          inventoryItemId: null,
          productId: selectedProduct.id,
          productTitle: selectedProduct.title,
          redoProductId: selectedProduct.id,
          sku: selectedProduct.sku ?? "",
          title: selectedProduct.title,
          variantId: null,
          variantTitle: "",
        });
      }
    },
  );

  return (
    <Flex dir="column" gap="none">
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        borderTopWidth="1px"
        dir="row"
        justify="space-between"
        p="3xl"
      >
        <Flex dir="column" gap="xs">
          <Text fontSize="md" fontWeight="semibold">
            Order items
          </Text>
        </Flex>
        <RedoButton
          hierarchy="secondary"
          onClick={addOrderItemClick}
          size="md"
          text="+ Add an item"
          theme="normal"
        />
      </Flex>
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        dir="row"
        justify="space-between"
        w="full"
      >
        <table className={styles.table}>
          <colgroup>
            <col className={styles.item} />
            <col className={styles.quantity} />
            <col className={styles.price} />
            <col className={styles.actions} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <Text fontSize="xs" textColor="tertiary">
                  Item
                </Text>
              </th>
              <th>
                <Text fontSize="xs" textColor="tertiary">
                  Quantity
                </Text>
              </th>
              <th>
                <Text fontSize="xs" textColor="tertiary">
                  Unit Price (USD)
                </Text>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>
                  <ProductSelector
                    allowCustom
                    multiSelect={false}
                    onSelectionChange={(products) => {
                      handleProductSelection(index, products);
                    }}
                    placeholder="Search products..."
                    selectedProducts={
                      item.title
                        ? [
                            (item.redoProductId ?? item.productId)
                              ? {
                                  id: (item.redoProductId ??
                                    item.productId) as ProductId,
                                  imageUrl: item.imageUrl,
                                  sku: item.sku ?? "",
                                  title: item.title,
                                }
                              : {
                                  isCustom: true as const,
                                  sku: item.sku ?? "",
                                  title: item.title,
                                },
                          ]
                        : []
                    }
                  />
                </td>
                <td>
                  <RedoIncrementDecrement
                    fullWidth
                    max={1000}
                    min={1}
                    setValue={(value) => {
                      updateItem(index, {
                        ...item,
                        totalQuantity: value,
                        unfulfilledQuantity: value,
                      });
                    }}
                    size="small"
                    value={item.totalQuantity}
                  />
                </td>
                <td>
                  <RedoIncrementDecrement
                    fullWidth
                    min={0.01}
                    setValue={(value) => {
                      updateItem(index, {
                        ...item,
                        unitPrice: {
                          amount: value.toString(),
                          currency: Currency.USD,
                        },
                      });
                    }}
                    size="small"
                    step={0.01}
                    value={Number(item.unitPrice.amount)}
                  />
                </td>
                <td>
                  <IconButton
                    onClick={() => removeItem(index)}
                    pt="sm"
                    size={ButtonSize.SMALL}
                  >
                    <XIcon />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Flex>
    </Flex>
  );
});
